import { useEffect, useState } from "react";
import { X, AlertCircle, Info, CheckCircle, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Announcement {
  id: string;
  title: string;
  message: string;
  announcement_type: string;
  priority: number;
  display_as_popup: boolean;
  pin_to_dashboard: boolean;
}

export const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [popupAnnouncement, setPopupAnnouncement] = useState<Announcement | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadAnnouncements();
    
    // Subscribe to new announcements
    const channel = supabase
      .channel("announcements")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "admin_announcements",
        },
        () => {
          loadAnnouncements();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadAnnouncements = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get active announcements
    const { data: allAnnouncements } = await supabase
      .from("admin_announcements")
      .select("*")
      .eq("is_active", true)
      .order("priority", { ascending: false });

    if (!allAnnouncements) return;

    // Check which ones user has read
    const { data: reads } = await supabase
      .from("announcement_reads")
      .select("announcement_id")
      .eq("user_id", user.id);

    const readIds = new Set(reads?.map(r => r.announcement_id) || []);
    const unreadAnnouncements = allAnnouncements.filter(a => !readIds.has(a.id));

    setAnnouncements(unreadAnnouncements);

    // Show popup for highest priority unread popup announcement
    const popupToShow = unreadAnnouncements
      .filter(a => a.display_as_popup)
      .sort((a, b) => b.priority - a.priority)[0];

    if (popupToShow) {
      setPopupAnnouncement(popupToShow);
    }
  };

  const markAsRead = async (announcementId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from("announcement_reads").insert({
      announcement_id: announcementId,
      user_id: user.id,
    });

    setDismissedIds(prev => new Set([...prev, announcementId]));
    
    if (popupAnnouncement?.id === announcementId) {
      setPopupAnnouncement(null);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "urgent":
        return <AlertTriangle className="h-5 w-5" />;
      case "warning":
        return <AlertCircle className="h-5 w-5" />;
      case "success":
        return <CheckCircle className="h-5 w-5" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  const getVariant = (type: string) => {
    switch (type) {
      case "urgent":
      case "warning":
        return "destructive";
      default:
        return "default";
    }
  };

  const pinnedAnnouncements = announcements
    .filter(a => a.pin_to_dashboard && !dismissedIds.has(a.id))
    .sort((a, b) => b.priority - a.priority);

  return (
    <>
      {/* Pinned Announcements Banner */}
      {pinnedAnnouncements.length > 0 && (
        <div className="space-y-2 mb-6">
          {pinnedAnnouncements.map((announcement) => (
            <Alert
              key={announcement.id}
              variant={getVariant(announcement.announcement_type)}
              className="relative"
            >
              <div className="flex items-start gap-3">
                {getIcon(announcement.announcement_type)}
                <div className="flex-1">
                  <AlertTitle className="mb-1">{announcement.title}</AlertTitle>
                  <AlertDescription>{announcement.message}</AlertDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => markAsRead(announcement.id)}
                  className="h-6 w-6"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </Alert>
          ))}
        </div>
      )}

      {/* Popup Announcement Dialog */}
      <Dialog open={!!popupAnnouncement} onOpenChange={(open) => {
        if (!open && popupAnnouncement) {
          markAsRead(popupAnnouncement.id);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {popupAnnouncement && getIcon(popupAnnouncement.announcement_type)}
              {popupAnnouncement?.title}
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              {popupAnnouncement?.message}
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => popupAnnouncement && markAsRead(popupAnnouncement.id)}>
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
